import { useState } from 'react';
import { API_BASE_URL } from './config';
import { useResponsive } from './hooks/useResponsive';

interface Property {
  id: number;
  created_at?: string;
  status?: string;
  host_name?: string;
  host_phone?: string;
  manager?: string;
  occupancy_status?: string;
  reservation_rate?: string;
  contract_count?: string;
  accumulated_settlement?: string;
  utilization_rate?: string;
  space: {
    road_address: string;
    building_type: string;
    room_count: number;
    exclusive_area: number;
  } | null;
  rent: {
    weekly_rent: number;
    deposit: number;
    weekly_maintenance_fee: number;
  } | null;
  descriptions: {
    room_name: string;
  } | null;
  photos: Array<{
    photo_url: string;
    display_order: number;
  }>;
}

interface AdminTabProps {
  properties: Property[];
  token: string;
  onClickProperty: (id: number) => void;
  onEditProperty: (id: number) => void;
  onUpdate: () => void;
  formatPrice: (price: number) => string;
  convertImageUrl: (url: string) => string;
}

const gridCols = '36px 64px 48px 1fr 68px 52px 52px 52px 68px 52px 68px 84px 68px 52px 136px';

const statusConfig: Record<string, { label: string; bg: string; color: string; small?: boolean }> = {
  'active':   { label: '등록',   bg: '#f1f5f9', color: '#475569' },
  'inactive': { label: '비활성', bg: '#f1f5f9', color: '#94a3b8' },
  '심사중':   { label: '심사중', bg: '#fff7ed', color: '#ea580c', small: true },
  '검수승인': { label: '승인',   bg: '#f0fdf4', color: '#16a34a' },
  '검수반려': { label: '반려',   bg: '#fef2f2', color: '#dc2626' },
  '검수보완': { label: '보완',   bg: '#fffbeb', color: '#d97706' },
};

interface EditingCell {
  propertyId: number;
  field: 'host_name' | 'host_phone' | 'manager' | 'occupancy_status' | 'reservation_rate' | 'contract_count' | 'accumulated_settlement' | 'utilization_rate';
}

type SortKey = 'created_at' | 'weekly_fee' | 'occupancy' | 'reservation_rate' | 'contract_count' | 'utilization_rate' | 'accumulated_settlement';
type SortDir = 'desc' | 'asc';

const sortOptions: { key: SortKey; desc: string; asc: string }[] = [
  { key: 'created_at', desc: '최신순', asc: '오래된순' },
  { key: 'weekly_fee', desc: '요금 높은순', asc: '요금 낮은순' },
  { key: 'occupancy', desc: '거주', asc: '공실' },
  { key: 'reservation_rate', desc: '예약률 높은순', asc: '예약률 낮은순' },
  { key: 'contract_count', desc: '계약 많은순', asc: '계약 적은순' },
  { key: 'utilization_rate', desc: '가동률 높은순', asc: '가동률 낮은순' },
  { key: 'accumulated_settlement', desc: '정산 높은순', asc: '정산 낮은순' },
];

const parseNum = (s: string) => { const n = parseFloat(s.replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };

export default function AdminTab({ properties, token, onClickProperty, onEditProperty, onUpdate, formatPrice, convertImageUrl }: AdminTabProps) {
  const { isMobile } = useResponsive();
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? properties.filter((p, i) => {
        const num = String(i + 1);
        const numDot = `${num}.`;
        const addr = (p.space?.road_address || '').toLowerCase();
        const name = (p.descriptions?.room_name || '').toLowerCase();
        const host = (p.host_name || '').toLowerCase();
        const phone = (p.host_phone || '').toLowerCase();
        return num === q || numDot.startsWith(q) || q.startsWith(numDot) || addr.includes(q) || name.includes(q) || host.includes(q) || phone.includes(q);
      })
    : properties;

  const getSuggestions = (): { label: string; type: string }[] => {
    if (!q || q.length < 1) return [];
    const seen = new Set<string>();
    const results: { label: string; type: string }[] = [];
    properties.forEach((p, i) => {
      const num = String(i + 1);
      const numDot = `${num}.`;
      if ((num === q || numDot.startsWith(q) || q.startsWith(numDot)) && !seen.has(num)) { seen.add(num); results.push({ label: `${num}. ${p.descriptions?.room_name || ''}`, type: '번호' }); }
      const addr = p.space?.road_address || '';
      if (addr.toLowerCase().includes(q) && !seen.has(addr)) { seen.add(addr); results.push({ label: addr, type: '주소' }); }
      const name = p.descriptions?.room_name || '';
      if (name.toLowerCase().includes(q) && !seen.has(name)) { seen.add(name); results.push({ label: name, type: '매물' }); }
      const host = p.host_name || '';
      if (host && host.toLowerCase().includes(q) && !seen.has(host)) { seen.add(host); results.push({ label: host, type: '호스트' }); }
      const phone = p.host_phone || '';
      if (phone && phone.toLowerCase().includes(q) && !seen.has(phone)) { seen.add(phone); results.push({ label: phone, type: '연락처' }); }
    });
    return results.slice(0, 8);
  };

  const suggestions = getSuggestions();

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = (() => {
    if (!sortKey) return filtered;
    const list = [...filtered];
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      let va = 0, vb = 0;
      switch (sortKey) {
        case 'created_at':
          va = a.created_at ? new Date(a.created_at).getTime() : 0;
          vb = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'weekly_fee':
          va = (a.rent?.weekly_rent || 0) + (a.rent?.weekly_maintenance_fee || 0);
          vb = (b.rent?.weekly_rent || 0) + (b.rent?.weekly_maintenance_fee || 0);
          break;
        case 'occupancy': {
          const occ = (s: string) => { const l = s.toLowerCase(); return l.includes('거주') || l.includes('입주') ? 1 : 0; };
          va = occ(a.occupancy_status || '');
          vb = occ(b.occupancy_status || '');
          break;
        }
        case 'reservation_rate':
          va = parseNum(a.reservation_rate || '');
          vb = parseNum(b.reservation_rate || '');
          break;
        case 'contract_count':
          va = parseNum(a.contract_count || '');
          vb = parseNum(b.contract_count || '');
          break;
        case 'utilization_rate':
          va = parseNum(a.utilization_rate || '');
          vb = parseNum(b.utilization_rate || '');
          break;
        case 'accumulated_settlement':
          va = parseNum(a.accumulated_settlement || '');
          vb = parseNum(b.accumulated_settlement || '');
          break;
      }
      return (va - vb) * dir;
    });
    return list;
  })();

  const startEdit = (propertyId: number, field: EditingCell['field'], currentValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing({ propertyId, field });
    setEditValue(currentValue);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await fetch(`${API_BASE_URL}/api/properties/${editing.propertyId}/admin-fields`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [editing.field]: editValue }),
      });
      onUpdate();
    } catch (err) {
      console.error('Failed to update:', err);
    }
    setEditing(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') setEditing(null);
  };

  const handleDelete = async (propertyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'deleted' }),
      });
      onUpdate();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleDuplicate = async (propertyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('이 매물을 복제하시겠습니까?\n(주소 끝번지 +5, 호수 +1)')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('duplicate failed');
      onUpdate();
    } catch (err) {
      console.error('Failed to duplicate:', err);
    }
  };

  const renderCell = (property: Property, field: EditingCell['field'], value: string) => {
    const isEditing = editing?.propertyId === property.id && editing?.field === field;

    if (isEditing) {
      return (
        <input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', padding: '4px 0', fontSize: '12px',
            border: '1.5px solid #3b82f6', borderRadius: '6px', outline: 'none',
            boxSizing: 'border-box', backgroundColor: '#eff6ff',
            fontFamily: 'inherit',
          }}
        />
      );
    }

    return (
      <span
        onClick={(e) => startEdit(property.id, field, value, e)}
        style={{
          display: 'block', fontSize: '12px', color: value ? '#334155' : '#cbd5e1',
          cursor: 'text', padding: '4px 0', borderRadius: '6px',
          border: '1px solid transparent', transition: 'all 0.15s ease',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {value || '-'}
      </span>
    );
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px' }}>
      {/* 상단: 제목 + 매물 수 */}
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.3px' }}>
          전체 매물 관리
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
          총 {properties.length}개 매물
        </p>
      </div>

      {isMobile ? (
        /* ── 모바일: 필터 가로스크롤 + 검색 별도 줄 ── */
        <>
          <div style={{ overflowX: 'auto', marginBottom: '8px', paddingBottom: '2px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap', width: 'max-content' }}>
              {sortOptions.map((opt) => {
                const active = sortKey === opt.key;
                const label = active ? (sortDir === 'desc' ? opt.desc : opt.asc) : opt.desc;
                return (
                  <button key={opt.key} onClick={() => toggleSort(opt.key)} style={{ padding: '5px 12px', fontSize: '12px', fontWeight: active ? '600' : '400', color: active ? '#3b82f6' : '#64748b', backgroundColor: active ? '#eff6ff' : '#f8fafc', border: `1px solid ${active ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: '20px', cursor: 'pointer', outline: 'none', lineHeight: '1.4', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {label}{active && <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortDir === 'desc' ? '▼' : '▲'}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <input value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }} onFocus={() => setShowSuggestions(true)} placeholder="번호, 주소, 매물명, 호스트, 연락처 검색" style={{ width: '100%', padding: '8px 12px 8px 32px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '20px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#f8fafc', color: '#334155' }} onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} />
            <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            {searchQuery && <span onClick={() => { setSearchQuery(''); setShowSuggestions(false); }} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: 1 }}>&times;</span>}
            {showSuggestions && suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '6px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', zIndex: 100, overflow: 'hidden' }}>
                {suggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => { setSearchQuery(s.label); setShowSuggestions(false); }} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '8px', flexShrink: 0, padding: '2px 6px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>{s.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        /* ── 데스크탑: 정렬 칩 + 검색 같은 줄 ── */
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {sortOptions.map((opt) => {
              const active = sortKey === opt.key;
              const label = active ? (sortDir === 'desc' ? opt.desc : opt.asc) : opt.desc;
              return (
                <button key={opt.key} onClick={() => toggleSort(opt.key)} style={{ padding: '5px 12px', fontSize: '12px', fontWeight: active ? '600' : '400', color: active ? '#3b82f6' : '#64748b', backgroundColor: active ? '#eff6ff' : '#f8fafc', border: `1px solid ${active ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s ease', outline: 'none', lineHeight: '1.4', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {label}{active && <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortDir === 'desc' ? '▼' : '▲'}</span>}
                </button>
              );
            })}
          </div>
          <div style={{ position: 'relative', width: '280px', flexShrink: 0 }}>
            <input value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }} onFocus={() => setShowSuggestions(true)} placeholder="번호, 주소, 매물명, 호스트, 연락처 검색" style={{ width: '100%', padding: '8px 12px 8px 32px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '20px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#f8fafc', transition: 'all 0.2s ease', color: '#334155' }} onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} />
            <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            {searchQuery && <span onClick={() => { setSearchQuery(''); setShowSuggestions(false); }} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: 1, width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>&times;</span>}
            {showSuggestions && suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '6px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,23,42,0.08)', zIndex: 100, overflow: 'hidden' }}>
                {suggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => { setSearchQuery(s.label); setShowSuggestions(false); }} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none', transition: 'background 0.1s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '8px', flexShrink: 0, padding: '2px 6px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>{s.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {properties.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <p style={{ color: '#94a3b8', fontSize: '15px', margin: 0 }}>등록된 매물이 없습니다</p>
        </div>
      ) : isMobile ? (
        /* ── 모바일 카드 레이아웃 ── */
        <div>
          {sorted.length === 0 && q && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '14px' }}>검색 결과가 없습니다.</div>}
          {sorted.map((property, index) => {
            const badge = statusConfig[property.status || ''] || { label: property.status || '-', bg: '#f1f5f9', color: '#64748b' };
            const originalIndex = properties.indexOf(property);
            const weeklyFee = (property.rent?.weekly_rent || 0) + (property.rent?.weekly_maintenance_fee || 0);
            return (
              <div
                key={property.id}
                onClick={() => onClickProperty(property.id)}
                style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: 10, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,.04)', cursor: 'pointer' }}
              >
                {/* 썸네일 + 매물명 + 상태 */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ width: 60, height: 48, borderRadius: 6, overflow: 'hidden', backgroundColor: '#f1f5f9', flexShrink: 0 }}>
                    {property.photos && property.photos.length > 0
                      ? <img src={convertImageUrl(property.photos[0].photo_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#cbd5e1' }}>No img</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {originalIndex + 1}. {property.descriptions?.room_name || '제목 없음'}
                      </span>
                      <span style={{ fontSize: 10, padding: badge.small ? '2px 6px' : '2px 8px', borderRadius: 12, backgroundColor: badge.bg, color: badge.color, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>{badge.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{property.space?.road_address || '-'}</div>
                  </div>
                </div>
                {/* 주요 지표 */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6, paddingLeft: 2 }}>
                  {weeklyFee > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{formatPrice(weeklyFee)}<span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 2 }}>/주</span></span>}
                  {property.occupancy_status && <span style={{ fontSize: 12, color: property.occupancy_status === '입주중' ? '#16a34a' : '#64748b' }}>{property.occupancy_status}</span>}
                  {property.utilization_rate && <span style={{ fontSize: 12, color: '#475569' }}>가동률 {property.utilization_rate}</span>}
                  {property.contract_count && <span style={{ fontSize: 12, color: '#475569' }}>계약 {property.contract_count}건</span>}
                  {property.accumulated_settlement && <span style={{ fontSize: 12, color: '#475569' }}>{property.accumulated_settlement}</span>}
                </div>
                {/* 호스트 + 날짜 + 버튼 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {property.host_name && <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{property.host_name}{property.host_phone ? ` · ${property.host_phone}` : ''}</span>}
                  {!property.host_name && property.created_at && <span style={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>{new Date(property.created_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })}</span>}
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <span onClick={(e) => { e.stopPropagation(); onEditProperty(property.id); }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, backgroundColor: '#eff6ff', color: '#3b82f6', cursor: 'pointer', fontWeight: 500 }}>수정</span>
                    <span onClick={(e) => handleDelete(property.id, e)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, backgroundColor: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontWeight: 500 }}>삭제</span>
                    <span onClick={(e) => handleDuplicate(property.id, e)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, backgroundColor: '#f5f3ff', color: '#8b5cf6', cursor: 'pointer', fontWeight: 500 }}>복제</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── 데스크탑 테이블 ── */
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.03)',
          overflow: 'hidden',
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols,
            padding: '12px 16px', backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontSize: '11px', fontWeight: '600', color: '#64748b',
            gap: '6px', alignItems: 'center',
            letterSpacing: '0.02em', textTransform: 'uppercase' as const,
          }}>
            <span style={{ textAlign: 'center' }}>No</span>
            <span>등록일</span>
            <span></span>
            <span>매물 정보</span>
            <span>1주요금</span>
            <span>상태</span>
            <span>예약률</span>
            <span>계약</span>
            <span>누적정산</span>
            <span>가동률</span>
            <span>호스트</span>
            <span>연락처</span>
            <span>담당자</span>
            <span style={{ textAlign: 'center', borderRight: '1px solid #e2e8f0', paddingRight: '6px' }}>검수</span>
            <span style={{ textAlign: 'center' }}>관리</span>
          </div>

          {/* 리스트 */}
          {sorted.length === 0 && q && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8', fontSize: '14px' }}>
              검색 결과가 없습니다.
            </div>
          )}
          {sorted.map((property, index) => {
            const badge = statusConfig[property.status || ''] || { label: property.status || '-', bg: '#f1f5f9', color: '#64748b' };
            const originalIndex = properties.indexOf(property);
            const isEven = index % 2 === 0;

            return (
              <div
                key={property.id}
                onClick={() => onClickProperty(property.id)}
                style={{
                  display: 'grid', gridTemplateColumns: gridCols,
                  padding: '14px 16px', borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer', transition: 'all 0.15s ease',
                  alignItems: 'center', gap: '6px',
                  backgroundColor: isEven ? '#fff' : '#fafbfc',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f7ff';
                  e.currentTarget.style.boxShadow = 'inset 3px 0 0 #3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isEven ? '#fff' : '#fafbfc';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* 번호 */}
                <span style={{
                  fontSize: '12px', fontWeight: '600', color: '#94a3b8', textAlign: 'center',
                  fontFeatureSettings: '"tnum"',
                }}>{originalIndex + 1}</span>

                {/* 등록일 */}
                <span style={{ fontSize: '11px', color: '#94a3b8', fontFeatureSettings: '"tnum"' }}>
                  {property.created_at ? new Date(property.created_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }) : '-'}
                </span>

                {/* 썸네일 */}
                <div style={{
                  width: '44px', height: '34px', borderRadius: '6px', overflow: 'hidden',
                  backgroundColor: '#f1f5f9', flexShrink: 0,
                }}>
                  {property.photos && property.photos.length > 0 ? (
                    <img
                      src={convertImageUrl(property.photos[0].photo_url)}
                      alt={property.descriptions?.room_name || ''}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '100%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '9px', color: '#cbd5e1',
                    }}>No img</div>
                  )}
                </div>

                {/* 매물 정보 + 주소 */}
                <div style={{ minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    lineHeight: '1.3',
                  }}>
                    {property.descriptions?.room_name || '제목 없음'}
                  </p>
                  <p style={{
                    margin: '3px 0 0', fontSize: '11px', color: '#94a3b8',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    lineHeight: '1.3',
                  }}>
                    {property.space?.road_address || '-'}
                  </p>
                </div>

                {/* 1주요금 = 임대료 + 관리비 */}
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                  {formatPrice((property.rent?.weekly_rent || 0) + (property.rent?.weekly_maintenance_fee || 0))}
                </span>

                {/* 상태(입주) - 자동 계산 */}
                <span style={{ fontSize: '11px', color: property.occupancy_status === '입주중' ? '#16a34a' : '#64748b' }}>
                  {property.occupancy_status || '-'}
                </span>

                {/* 예약률 */}
                {renderCell(property, 'reservation_rate', property.reservation_rate || '')}

                {/* 계약 - 자동 계산 */}
                <span style={{ fontSize: '11px', color: '#334155' }}>
                  {property.contract_count || '-'}
                </span>

                {/* 누적정산 - 자동 계산 */}
                <span style={{ fontSize: '11px', color: '#334155' }}>
                  {property.accumulated_settlement || '-'}
                </span>

                {/* 가동률 - 자동 계산 */}
                <span style={{ fontSize: '11px', color: '#334155' }}>
                  {property.utilization_rate || '-'}
                </span>

                {/* 호스트 */}
                {renderCell(property, 'host_name', property.host_name || '')}

                {/* 연락처 */}
                {renderCell(property, 'host_phone', property.host_phone || '')}

                {/* 담당자 */}
                {renderCell(property, 'manager', property.manager || '')}

                {/* 상태 */}
                <div style={{ textAlign: 'center', borderRight: '1px solid #e2e8f0', paddingRight: '6px' }}>
                  <span style={{
                    display: 'inline-block', padding: badge.small ? '3px 6px' : '3px 10px',
                    backgroundColor: badge.bg, color: badge.color,
                    borderRadius: '20px', fontSize: badge.small ? '9px' : '11px', fontWeight: '600',
                    lineHeight: '1.4', letterSpacing: '-0.01em', whiteSpace: 'nowrap',
                  }}>
                    {badge.label}
                  </span>
                </div>

                {/* 관리 */}
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                  <span
                    onClick={(e) => { e.stopPropagation(); onEditProperty(property.id); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '4px 8px', fontSize: '11px', fontWeight: '500',
                      color: '#3b82f6', backgroundColor: '#eff6ff',
                      borderRadius: '6px', cursor: 'pointer',
                      transition: 'all 0.15s ease', border: '1px solid transparent',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    수정
                  </span>
                  <span
                    onClick={(e) => handleDelete(property.id, e)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '4px 8px', fontSize: '11px', fontWeight: '500',
                      color: '#ef4444', backgroundColor: '#fef2f2',
                      borderRadius: '6px', cursor: 'pointer',
                      transition: 'all 0.15s ease', border: '1px solid transparent',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    삭제
                  </span>
                  <span
                    onClick={(e) => handleDuplicate(property.id, e)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '4px 8px', fontSize: '11px', fontWeight: '500',
                      color: '#8b5cf6', backgroundColor: '#f5f3ff',
                      borderRadius: '6px', cursor: 'pointer',
                      transition: 'all 0.15s ease', border: '1px solid transparent',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ede9fe'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f5f3ff'; e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    복제
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
